import { useCallback, useMemo, useRef } from 'react';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';

import type { Edge, Node } from '@xyflow/react';
import type { ICustomEdge } from '../interfaces/ICustomEdge';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';

interface IEdgeVisibilityContext {
  key: string,
  nodeById: Map<string, Node>,
  countryGroupLeaderByGroupId: Map<string, string>,
}

// Edge styling only depends on grouping/nesting topology, not on positions or viewport
// culling (which ReactFlow handles at render time). Caching the lookup maps behind a narrow
// key keeps updateEdgeStyles referentially stable during drag/pan, so styledEdges in the view
// is not recomputed on every nodes change.
const useValueChainEdgeStyles = (nodes: Node[]) => {
  const edgeVisibilityContextRef = useRef<IEdgeVisibilityContext | undefined>(undefined);

  const edgeVisibilityContext = useMemo(() => {
    const unitNodes = nodes.filter((node) => node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT);
    const key = unitNodes
      .map((unitNode) => {
        const unitData = unitNode.data as unknown as ICustomUnitNodeData;
        return [
          unitNode.id,
          unitNode.parentId ?? '',
          unitData.countryGroupId ?? '',
          unitData.isCountryGroupLeader ? '1' : '0',
          unitData.isHiddenByParent ? '1' : '0',
        ].join(':');
      })
      .join('|');

    const prevContext = edgeVisibilityContextRef.current;
    if (prevContext && prevContext.key === key) {
      return prevContext;
    }

    const nodeById = new Map<string, Node>(
      unitNodes.map((unitNode) => [unitNode.id, unitNode]),
    );
    const countryGroupLeaderByGroupId = new Map<string, string>();

    unitNodes.forEach((unitNode) => {
      const unitData = unitNode.data as unknown as ICustomUnitNodeData;
      if (unitData.countryGroupId && unitData.isCountryGroupLeader) {
        countryGroupLeaderByGroupId.set(unitData.countryGroupId, unitNode.id);
      }
    });

    const nextContext = { key, nodeById, countryGroupLeaderByGroupId };
    edgeVisibilityContextRef.current = nextContext;
    return nextContext;
  }, [nodes]);

  const styledEdgeCacheRef = useRef(new Map<string, { input: Edge, output: Edge }>());

  const updateEdgeStyles = useCallback((edgesData: Edge[]): Edge[] => {
    const { nodeById, countryGroupLeaderByGroupId } = edgeVisibilityContext;

    const getVisibleUnitId = (nodeId: string): { id: string, isAggregated: boolean } => {
      const initialNode = nodeById.get(nodeId);

      if (!initialNode) {
        return {
          id: nodeId,
          isAggregated: false,
        };
      }

      let currentNode: Node | undefined = initialNode;
      let currentData = currentNode.data as unknown as ICustomUnitNodeData;
      let isAggregated = false;

      if (currentData.countryGroupId) {
        const leaderId = countryGroupLeaderByGroupId.get(currentData.countryGroupId);

        if (leaderId && leaderId !== currentNode.id) {
          const leaderNode = nodeById.get(leaderId);
          if (leaderNode) {
            currentNode = leaderNode;
            currentData = leaderNode.data as unknown as ICustomUnitNodeData;
            isAggregated = true;
          }
        }
      }

      while (
        currentData.isHiddenByParent
        && typeof currentNode?.parentId === 'string'
        && currentNode.parentId.startsWith('unit_')
      ) {
        const parentNode = nodeById.get(currentNode.parentId);

        if (!parentNode) {
          break;
        }

        currentNode = parentNode;
        currentData = parentNode.data as unknown as ICustomUnitNodeData;
        isAggregated = true;
      }

      return {
        id: currentNode?.id || nodeId,
        isAggregated,
      };
    };

    const prevStyledEdgeCache = styledEdgeCacheRef.current;
    const nextStyledEdgeCache = new Map<string, { input: Edge, output: Edge }>();

    const styledEdges = edgesData.map((edgeItem) => {
      const originalSource = edgeItem.source;
      const originalTarget = edgeItem.target;
      const sourceMeta = getVisibleUnitId(originalSource);
      const targetMeta = getVisibleUnitId(originalTarget);

      const nextSource = sourceMeta.id;
      const nextTarget = targetMeta.id;

      const isAggregatedConnection = sourceMeta.isAggregated || targetMeta.isAggregated;
      // Hide only edges that collapse onto a single aggregated node (self-loops). Viewport
      // visibility of spanning edges is left to ReactFlow so a line crossing the viewport
      // stays drawn even when both endpoint cards are off-screen.
      const isHidden = nextSource === nextTarget;

      const cached = prevStyledEdgeCache.get(edgeItem.id);
      if (cached && cached.input === edgeItem) {
        const cachedData = cached.output.data as Record<string, unknown> | undefined;
        const isCacheValid = cached.output.source === nextSource
          && cached.output.target === nextTarget
          && cached.output.hidden === isHidden
          && cachedData?.isAggregatedForCollapsed === isAggregatedConnection;

        if (isCacheValid) {
          nextStyledEdgeCache.set(edgeItem.id, cached);
          return cached.output;
        }
      }

      const nextData: Partial<ICustomEdge['data']> & Record<string, unknown> = {
        ...(edgeItem.data as Record<string, unknown> | undefined),
        isAggregatedForCollapsed: isAggregatedConnection,
        originalSource,
        originalTarget,
      };

      const output: Edge = {
        ...edgeItem,
        source: nextSource,
        target: nextTarget,
        animated: false,
        selectable: false,
        hidden: isHidden,
        data: nextData,
      };

      nextStyledEdgeCache.set(edgeItem.id, { input: edgeItem, output });
      return output;
    });

    styledEdgeCacheRef.current = nextStyledEdgeCache;
    return styledEdges;
  }, [edgeVisibilityContext]);

  return { updateEdgeStyles };
};

export default useValueChainEdgeStyles;
